/**
 * @lodex-pro/web-portal/hooks/useTankSelections — operator portal rework PR 8: the tanks a
 * pumper or foreman looks after ("My tanks"), whom the caller may choose tanks for, and the
 * tanks nobody looks after yet (coverage).
 *
 * Reads go through `useApiQuery` like the rest of the portal. The write (POST /my-tanks)
 * uses `useMutation` over `authedFetch` and throws the same `ApiError` (status + catalog
 * code) as the PR 7 writes, so the page can say what went wrong in words. A selection
 * changes what a pumper or foreman sees in Tickets, Volumes and Tanks, so every write also
 * refreshes those reads, not only the selection ones.
 *
 * Types only from @lodex-pro/shared (DEC-149 §9).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  SelectableTankView,
  SelectionPersonView,
  SelectTanksRequest,
  UncoveredTankView,
} from '@lodex-pro/shared/validation/operator-portal-routes';
import { REQUESTOR_OFFERS_KEY } from './useRequestorLinks';

const BASE = '/v1/operator-portal';

// ── Query keys ───────────────────────────────────────────────────────────────

export const MY_TANKS_KEY: readonly string[] = ['operator-portal', 'my-tanks'];
export const SELECTABLE_TANKS_KEY = (forUserId: string | undefined): readonly string[] => [
  ...MY_TANKS_KEY,
  'tanks',
  forUserId ?? 'me',
];
export const SELECTION_PEOPLE_KEY: readonly string[] = [...MY_TANKS_KEY, 'people'];
export const COVERAGE_KEY: readonly string[] = ['operator-portal', 'coverage'];

/** The reads whose rows depend on a pumper's or foreman's selections (keys from useOperatorPortal). */
const SCOPED_READ_KEYS: readonly (readonly string[])[] = [
  ['operator-portal', 'tickets'],
  ['operator-portal', 'ticket'],
  ['operator-portal', 'volumes'],
  ['operator-portal', 'tanks'],
];

// ── Reads ────────────────────────────────────────────────────────────────────

export interface SelectableTanksResponse {
  readonly forUserId: string;
  readonly tanks: SelectableTankView[];
}

/**
 * The company's tanks as one person sees them (default: the caller). `enabled` is false while
 * an admin has not yet chosen a person (an admin has no list of his own).
 */
export function useSelectableTanks(
  forUserId: string | undefined,
  enabled = true,
): ReturnType<typeof useApiQuery<SelectableTanksResponse>> {
  const opts = useApiOpts();
  const qs = forUserId === undefined ? '' : `?forUserId=${encodeURIComponent(forUserId)}`;
  return useApiQuery<SelectableTanksResponse>(opts, `${BASE}/my-tanks${qs}`, {
    queryKey: SELECTABLE_TANKS_KEY(forUserId),
    staleTime: 30_000,
    enabled,
  });
}

/** Whom the caller may choose tanks for. A pumper's page passes `enabled: false`. */
export function useSelectionPeople(
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ people: SelectionPersonView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ people: SelectionPersonView[] }>(opts, `${BASE}/my-tanks/people`, {
    queryKey: SELECTION_PEOPLE_KEY,
    staleTime: 30_000,
    enabled,
  });
}

/** Tanks with no contact: the admin's whole company, a foreman's routes. Pumpers: off. */
export function useCoverage(
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ uncovered: UncoveredTankView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ uncovered: UncoveredTankView[] }>(opts, `${BASE}/coverage`, {
    queryKey: COVERAGE_KEY,
    staleTime: 30_000,
    enabled,
  });
}

// ── Write ────────────────────────────────────────────────────────────────────

export interface SelectTanksVars {
  readonly forUserId?: string;
  readonly tankIds?: string[];
  readonly wellIds?: string[];
  readonly selected: boolean;
}

/** Select or drop tanks (or every tank on some wells) for the caller or `forUserId`. */
export function useSelectTanks(): UseMutationResult<{ changed: number }, Error, SelectTanksVars> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<{ changed: number }, Error, SelectTanksVars>({
    mutationFn: async (vars: SelectTanksVars): Promise<{ changed: number }> => {
      const body: Partial<SelectTanksRequest> = { ...vars };
      const res = await authedFetch(opts, `${BASE}/my-tanks`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        throw await apiErrorFromResponse(res, '/my-tanks');
      }
      return (await res.json()) as { changed: number };
    },
    onSettled: (): void => {
      void qc.invalidateQueries({ queryKey: MY_TANKS_KEY });
      void qc.invalidateQueries({ queryKey: COVERAGE_KEY });
      void qc.invalidateQueries({ queryKey: REQUESTOR_OFFERS_KEY });
      for (const key of SCOPED_READ_KEYS) void qc.invalidateQueries({ queryKey: key });
    },
  });
}
